import type { FocusEvent, ChangeEvent } from 'react';
import { Column, Columns, breakpoints, Stack, RadioGroup, Inline, Radio } from '@etg/wings';
import { ExpandableContent } from '@eti/components';
import { ConnectedInput, ConnectedRadioGroup } from '@eti/form';
import { useProperty, useTranslation } from '@eti/providers';
import { constants, persistInputData } from '@eti/utils';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { formValueSelector, type FormStateMap } from 'redux-form';
import {
  CONTACT_INFO_FORM_SECTION,
  contactFields,
  contactTypes,
  fieldRestrictions,
} from '../../constants/formFields';

const { MOUSE_FLOW_CLASS_NAMES } = constants;
const columnWidth = { [breakpoints._0]: '4/4', [breakpoints._768]: '2/4' } as const;

type ContactType = (typeof contactTypes)[keyof typeof contactTypes];

interface ConnectedProps {
  selectedContactType: ContactType;
}

const mapStateToProps: MapStateToProps<ConnectedProps, { form: string }, { form: FormStateMap }> = (
  state,
  { form },
) => ({
  selectedContactType: formValueSelector(form)(
    state,
    `${CONTACT_INFO_FORM_SECTION}.${contactFields.CONTACT_TYPE}`,
  ),
});

interface TravelArrangerProps extends ConnectedProps {
  form: string;
}

const TravelArranger = ({ form, selectedContactType }: TravelArrangerProps) => {
  const { p } = useProperty();
  const { t, nonDebugT } = useTranslation();
  const shouldPersistData = p('IbeClient.TravelerDetails.PersistFormData.Enabled');
  const showLastNameBeforeFirstName = p('IbeClient.ShowLastNameBeforeFirstName');

  const radios = [
    {
      id: contactTypes.TRAVELER,
      value: contactTypes.TRAVELER,
      label: t('TravelerDetails.TravelArranger.Traveler.Label'),
    },
    {
      id: contactTypes.ARRANGER,
      value: contactTypes.ARRANGER,
      label: t('TravelerDetails.TravelArranger.Arranger.Label'),
    },
  ];

  const handleOnContactTypeChange = shouldPersistData
    ? (_event: ChangeEvent<HTMLInputElement> | undefined, value: ContactType) => {
        persistInputData(form, `${contactFields.CONTACT_TYPE}`)(value);
      }
    : undefined;

  const handleOnFirstNameChange = shouldPersistData
    ? (event: FocusEvent<HTMLInputElement>) =>
        persistInputData(form, `${contactFields.ARRANGER_FIRST_NAME}`)(event.target.value)
    : undefined;

  const handleOnLastNameChange = shouldPersistData
    ? (event: FocusEvent<HTMLInputElement>) =>
        persistInputData(form, `${contactFields.ARRANGER_LAST_NAME}`)(event.target.value)
    : undefined;

  const inputs = [
    <Column key="1" width={columnWidth}>
      <ConnectedInput
        autoCapitalize="on"
        autoComplete="given-name"
        autoCorrect="off"
        className={MOUSE_FLOW_CLASS_NAMES}
        data-testid="travelArranger-firstName"
        label={t('TravelerDetails.TravelArranger.FirstName.Label')}
        maxLength={fieldRestrictions.MAX_CHARS}
        name={contactFields.ARRANGER_FIRST_NAME}
        onBlur={handleOnFirstNameChange}
        placeholder={t('TravelerDetails.TravelArranger.FirstName.Placeholder')}
        shouldForceCapitalize={p('BookingFlow.Inputs.Capitalize.Enabled')}
        shouldMarkInputAsRequired
        spellCheck="false"
        validationRuleName={contactFields.ARRANGER_FIRST_NAME}
      />
    </Column>,
    <Column key="2" width={columnWidth}>
      <ConnectedInput
        autoCapitalize="on"
        autoComplete="family-name"
        autoCorrect="off"
        className={MOUSE_FLOW_CLASS_NAMES}
        data-testid="travelArranger-lastName"
        label={t('TravelerDetails.TravelArranger.LastName.Label')}
        maxLength={fieldRestrictions.MAX_CHARS}
        name={contactFields.ARRANGER_LAST_NAME}
        onBlur={handleOnLastNameChange}
        placeholder={t('TravelerDetails.TravelArranger.LastName.Placeholder')}
        shouldForceCapitalize={p('BookingFlow.Inputs.Capitalize.Enabled')}
        shouldMarkInputAsRequired
        spellCheck="false"
        validationRuleName={contactFields.ARRANGER_LAST_NAME}
      />
    </Column>,
  ];

  return (
    <>
      <Columns spacing={24}>
        <Column>
          <ConnectedRadioGroup
            aria-labelledby="arranger-radiogroup-legend"
            name={contactFields.CONTACT_TYPE}
            onChange={handleOnContactTypeChange}
            required
            role="radiogroup"
          >
            <Stack spacing={8}>
              <RadioGroup.Legend id="arranger-radiogroup-legend">
                {nonDebugT('Input.Field.Required', t('TravelerDetails.TravelArranger.Heading'))}
              </RadioGroup.Legend>
              <Inline spacing={24}>
                {radios.map((radio) => (
                  <Radio.Label key={radio.id}>
                    <Inline alignY="center" spacing={8}>
                      <Radio
                        data-testid={`travelerDetails-travelArranger-${radio.id}`}
                        id={radio.id}
                        name="travel-arranger-radio-group"
                        value={radio.value}
                      />
                      {radio.label}
                    </Inline>
                  </Radio.Label>
                ))}
              </Inline>
            </Stack>
          </ConnectedRadioGroup>
        </Column>
      </Columns>
      <ExpandableContent isExpanded={selectedContactType === contactTypes.ARRANGER}>
        <Columns spacing={24}>{showLastNameBeforeFirstName ? inputs.reverse() : inputs}</Columns>
      </ExpandableContent>
    </>
  );
};

export default connect(mapStateToProps)(TravelArranger);
